import { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, CheckCircle, Upload, Database } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface DataRestorationProps {
  accessToken: string;
}

export function DataRestoration({ accessToken }: DataRestorationProps) {
  const [companyId, setCompanyId] = useState('79fe648e-866f-4c0e-86da-f60894033c57');
  const [jsonData, setJsonData] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRestore = async () => {
    setRestoring(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate JSON
      let parsedData;
      try {
        parsedData = JSON.parse(jsonData);
      } catch (e) {
        throw new Error('Invalid JSON format. Please check your data and try again.');
      }

      // Validate that it has the expected structure
      if (!parsedData.projects && !parsedData.sows) {
        throw new Error('Data must contain "projects" and/or "sows" fields');
      }

      // Send restoration request
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            ...parsedData,
            companyId: companyId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to restore data');
      }

      setSuccess(true);
      setJsonData('');
      
      setTimeout(() => {
        window.location.reload(); // Reload to show restored data
      }, 2000);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setRestoring(false);
    }
  };

  const loadSampleStructure = () => {
    const sample = {
      projects: [
        {
          id: Date.now().toString(),
          name: "Sample Space",
          description: "Sample Jira space",
          projectKey: "SAMPLE",
          projectType: "jsm",
          workflows: [],
          workTypes: [],
          teams: []
        }
      ],
      sows: []
    };
    setJsonData(JSON.stringify(sample, null, 2));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <Card className="border-yellow-200 bg-yellow-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-yellow-900">
            <Database className="h-5 w-5" />
            Data Restoration Tool
          </CardTitle>
          <CardDescription className="text-yellow-800">
            Restore company configuration data from a backup JSON file
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="border-yellow-300 bg-yellow-100">
            <AlertCircle className="h-4 w-4 text-yellow-700" />
            <AlertDescription className="text-yellow-800">
              <strong>Warning:</strong> This will overwrite all existing data for the specified company.
              Make sure you have the correct backup data before proceeding.
            </AlertDescription>
          </Alert>

          <div>
            <Label htmlFor="restoreCompanyId">Company ID</Label>
            <Input
              id="restoreCompanyId"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              placeholder="Enter company ID"
              className="mt-1"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="jsonData">Backup JSON Data</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={loadSampleStructure}
                type="button"
              >
                Load Sample Structure
              </Button>
            </div>
            <Textarea
              id="jsonData"
              value={jsonData}
              onChange={(e) => setJsonData(e.target.value)}
              placeholder='{"projects": [...], "sows": [...]}'
              className="font-mono text-sm min-h-[300px]"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Paste the complete JSON backup here. Must include "projects" and "sows" arrays.
            </p>
          </div>

          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Data restored. Reloading page...
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleRestore}
              disabled={restoring || !companyId || !jsonData}
              className="flex-1"
            >
              <Upload className="h-4 w-4 mr-2" />
              {restoring ? 'Restoring...' : 'Restore Data'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>If you have a backup of your company configuration data:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Enter the Company ID for the company you want to restore</li>
            <li>Paste the complete backup JSON data into the text area</li>
            <li>Verify the data looks correct</li>
            <li>Click "Restore Data"</li>
          </ol>
          <p className="mt-4 text-muted-foreground">
            The backup data should be a JSON object with "projects" and "sows" arrays,
            matching the structure used by the Service Desk Request Form Builder.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
